import { yupResolver } from '@hookform/resolvers/yup';
import { Redirect, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import {
  FormProvider,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAvoidingView from '@/components/Custom/ModalKeyboardAvoidingView';
import {
  EventRsvpEditorAccessFragment,
  getEventRsvpEditorAccess,
} from '@/components/Event/EventRsvpEditorAccess';
import {
  isRichTextEmpty,
  isRsvpNoteWithinLengthLimit,
  RSVP_NOTE_MAX_LENGTH,
  trimRichText,
} from '@/components/RichText/RichText.utils';
import RichTextComposer, {
  RichTextComposerEditor,
  RichTextComposerToolbar,
} from '@/components/RichText/RichTextComposer';
import Box from '@/components/UI/Box';
import ContentBoundary from '@/components/UI/ContentBoundary';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useCustomHeaderHeight from '@/hooks/useCustomHeaderHeight';
import useUserId from '@/hooks/useUserId';

const GetEventRsvpNotes = graphql(/* GraphQL */ `
  query GetEventRsvpNotes($eventId: uuid!, $subjectPersonId: String!) {
    person_by_pk(id: $subjectPersonId) {
      id
      name
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        event_id
        notes
        person_id
      }
    }
    ...EventRsvpEditorAccess
  }
`, [EventRsvpEditorAccessFragment]);

const UpsertEventRsvpNotes = graphql(/* GraphQL */ `
  mutation UpsertEventRsvpNotes(
    $eventId: uuid!
    $notes: String
    $personId: String!
  ) {
    insert_rsvp_one(
      object: { event_id: $eventId, notes: $notes, person_id: $personId }
      on_conflict: { constraint: rsvp_pkey, update_columns: [notes] }
    ) {
      event_id
      notes
      person_id
      status
      person {
        id
        event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
          event_id
          notes
          person_id
          status
        }
      }
    }
  }
`);

const NOTE_LENGTH_ERROR_MESSAGE = 'RSVP notes are limited to 2,000 characters.';

type EventRsvpNotesFormValues = {
  notes: string;
};

const EventRsvpNotesSchema: Yup.ObjectSchema<EventRsvpNotesFormValues> =
  Yup.object({
    notes: Yup.string()
      .defined()
      .transform(trimRichText)
      .test(
        'max-character-length',
        NOTE_LENGTH_ERROR_MESSAGE,
        isRsvpNoteWithinLengthLimit,
      ),
  });

function EventRsvpNotesComposer({
  eventId,
  initialValue,
  personId,
  personName,
}: {
  eventId: string;
  initialValue: string;
  personId: string;
  personName: string;
}) {
  const router = useRouter();
  const headerHeight = useCustomHeaderHeight();
  const [, saveNotes] = useMutation(UpsertEventRsvpNotes);
  const form = useForm<EventRsvpNotesFormValues>({
    defaultValues: { notes: initialValue },
    mode: 'onChange',
    resolver: yupResolver(EventRsvpNotesSchema),
  });
  const notesController = useController({
    control: form.control,
    name: 'notes',
  });
  const notes = useWatch({ control: form.control, name: 'notes' });
  const normalizedNotes = trimRichText(notes);
  const noteCharacterCount = Array.from(normalizedNotes).length;
  const isNoteTooLong = noteCharacterCount > RSVP_NOTE_MAX_LENGTH;
  const errorMessage = notesController.fieldState.error?.message ?? '';

  const onSave = async (values: EventRsvpNotesFormValues) => {
    const normalizedNotes = trimRichText(values.notes);
    await saveNotes({
      eventId,
      notes: isRichTextEmpty(normalizedNotes) ? null : normalizedNotes,
      personId,
    });
    router.back();
  };
  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: personName ? `${personName}'s RSVP Note` : 'RSVP Note',
        }}
      />
      <RichTextComposer
        accessibilityLabel={`RSVP note for ${personName}`}
        autoFocus
        defaultValue={initialValue}
        errorMessage={errorMessage}
        onChangeHtml={notesController.field.onChange}
        placeholder="Enter your notes here"
      >
        <Box flex style={style.contentContainer}>
          <ModalKeyboardAvoidingView
            automaticOffset={process.env.EXPO_OS !== 'android'}
            keyboardVerticalOffset={
              process.env.EXPO_OS === 'android' ? headerHeight : 0
            }
          >
            <RichTextComposerEditor />
            <RichTextComposerToolbar>
              <Box alignItems="center" space="xs" style={style.visibilityNote}>
                <Box
                  alignItems="center"
                  direction="horizontal"
                  justify="center"
                  space="xs"
                >
                  <Icon
                    name="information-circle-outline"
                    size="xs"
                    variant="typographySecondary"
                  />
                  <Text
                    center
                    flexShrink={1}
                    size="xs"
                    variant="typographySecondary"
                  >
                    Anyone on your team can see this note
                  </Text>
                </Box>
                <Text
                  center
                  size="xs"
                  variant={isNoteTooLong ? 'error' : 'typographySecondary'}
                >
                  {noteCharacterCount.toLocaleString()}/
                  {RSVP_NOTE_MAX_LENGTH.toLocaleString()} characters
                </Text>
              </Box>
            </RichTextComposerToolbar>
            <FormSaveButton
              info={{ text: 'Save', submittingText: 'Saving…' }}
              onPress={form.handleSubmit(onSave)}
            />
          </ModalKeyboardAvoidingView>
        </Box>
      </RichTextComposer>
    </FormProvider>
  );
}

export default function EventRsvpNotes() {
  const { eventId, personId: subjectPersonId } = useLocalSearchParams<{
    eventId: string;
    personId: string;
  }>();
  const personId = useUserId();
  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetEventRsvpNotes,
    variables: { eventId, subjectPersonId },
  });
  const access = getEventRsvpEditorAccess({
    fragment: data,
    personId,
    subjectPersonId,
  });
  const person = data?.person_by_pk;
  const rsvp = person?.event_rsvp[0];
  const unavailable = (
    <Redirect
      href={{
        pathname: '/event/[eventId]/rsvp',
        params: { eventId, subjectPersonId: personId },
      }}
    />
  );
  return (
    <>
      <Stack.Screen options={{ title: 'RSVP Note' }} />
      <ContentBoundary
        data={person}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={unavailable}
        errorMessage="Could not load this RSVP note."
      >
        {(person) =>
          access.canEdit ? (
            <EventRsvpNotesComposer
              eventId={eventId}
              initialValue={rsvp?.notes ?? ''}
              key={`${eventId}:${subjectPersonId}`}
              personId={subjectPersonId}
              personName={person.name ?? ''}
            />
          ) : (
            unavailable
          )
        }
      </ContentBoundary>
    </>
  );
}

const style = StyleSheet.create((_, rt) => ({
  visibilityNote: {
    width: '100%',
  },
  contentContainer: {
    paddingBottom: rt.insets.ime > 0 ? 0 : rt.insets.bottom,
  },
}));
